import React, { forwardRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { FaFacebookF, FaInstagram, FaDiscord, FaPhoneAlt, FaLinkedinIn } from 'react-icons/fa';
import { FaUpwork } from 'react-icons/fa6';
import { SiGmail } from 'react-icons/si';
import { FiGithub } from 'react-icons/fi';
import { TbBrandFiverr } from 'react-icons/tb';

import { useTheme } from '../contexts/ThemeProvider';
import { sendIdea } from '../utils/store/slices/sendSice';
import { copyToClipboard, notify, notYetAvailable } from '../utils/copyToClipboard';
import { profile } from '../data/profile';

const Contact = forwardRef((props, ref) => {
	const color = useTheme();
	const dispatch = useDispatch();

	const [inputValues, setInputValues] = useState({ name: '', email: '', message: '' });
	const [isLoading, setIsLoading] = useState(false);

	const inputChangeHandler = (e) => {
		setInputValues({ ...inputValues, [e.target.name]: e.target.value });
	};

	const submitIdeaHandler = (e) => {
		e.preventDefault();
		setIsLoading(true);

		dispatch(sendIdea(inputValues)).then((response) => {
			const payload = response.payload;
			setIsLoading(false);

			if (payload?.status) {
				notify.success(payload.message);
				setInputValues({ name: '', email: '', message: '' });
			} else {
				notify.error(payload?.message || `Couldn't send your message. Please email me at ${profile.email}.`);
			}
		});
	};

	return (
		<>
			{isLoading && (
				<LoadingContainer color={color.color}>
					<div className="lds-ellipsis">
						<div></div>
						<div></div>
						<div></div>
					</div>
					<p style={{ marginTop: '10px' }}>Sending your idea...</p>
				</LoadingContainer>
			)}

			<ContactContainer color={color.color} ref={ref} id="contact">
				<div className="sendIdea">
					<div className="header">
						<h1>Have an idea?</h1>
						<h3>tell me about it!</h3>
					</div>
					<form action="#" className="idea-form" onSubmit={submitIdeaHandler}>
						<input autoComplete="off" type="text" name="name" placeholder="Name" required value={inputValues.name} onChange={inputChangeHandler} />
						<input autoComplete="off" type="email" name="email" placeholder="Email" required value={inputValues.email} onChange={inputChangeHandler} />
						<textarea name="message" placeholder="Tell me your great idea here!" required value={inputValues.message} onChange={inputChangeHandler}></textarea>
						<button type="submit">SUBMIT</button>
					</form>
				</div>
				<div className="contactSocials">
					<div className="header">
						<h1>Contact me!</h1>
						<h3>here are my socials</h3>
					</div>
					<div className="socials">
						<div className="social">
							<a href={profile.links.facebook} target="_blank" rel="noreferrer" className="link">
								<FaFacebookF />
								Facebook
							</a>
							<a href={profile.links.github} target="_blank" rel="noreferrer" className="link">
								<FiGithub />
								Github
							</a>
							<a href={profile.links.instagram} target="_blank" rel="noreferrer" className="link">
								<FaInstagram />
								Instagram
							</a>
							<a href={profile.links.linkedin} target="_blank" rel="noreferrer" className="link">
								<FaLinkedinIn />
								LinkedIn
							</a>
							<button className="link" onClick={() => copyToClipboard(profile.discord, 'Discord Username')}>
								<FaDiscord />
								Discord
							</button>
							<button className="link" onClick={notYetAvailable}>
								<FaUpwork />
								UpWork
							</button>
							<button className="link" onClick={() => copyToClipboard(profile.phone, 'Phone Number')}>
								<FaPhoneAlt />
								Phone Number
							</button>
							<button className="link" onClick={notYetAvailable}>
								<TbBrandFiverr />
								Fiverr
							</button>
							<button className="link last-link" onClick={() => copyToClipboard(profile.email, 'Gmail')}>
								<SiGmail />
								Email Address
							</button>
						</div>
					</div>
				</div>
			</ContactContainer>
		</>
	);
});

export default Contact;

const LoadingContainer = styled.div`
	position: fixed;
	inset: 0;
	z-index: 2000;
	background-color: ${({ color }) => color.background};
	width: 100vw;
	height: 100vh;
	display:flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	.lds-ellipsis {
		display: inline-block;
		position: relative;
		width: 80px;
		height: 80px;
	}

	.lds-ellipsis div {
		position: absolute;
		top: 33px;
		width: 13px;
		height: 13px;
		border-radius: 50%;
		background: ${({ color }) => color.text};
		animation-timing-function: cubic-bezier(0, 1, 1, 0);
	}

	.lds-ellipsis div:nth-child(1) {
		left: 8px;
		animation: lds-ellipsis1 0.6s infinite;
	}

	.lds-ellipsis div:nth-child(2) {
		left: 8px;
		animation: lds-ellipsis2 0.6s infinite;
	}

	.lds-ellipsis div:nth-child(3) {
		left: 32px;
		animation: lds-ellipsis2 0.6s infinite;
	}

	.lds-ellipsis div:nth-child(4) {
		left: 56px;
		animation: lds-ellipsis3 0.6s infinite;
	}

	@keyframes lds-ellipsis1 {
		0% {
			transform: scale(0);
		}

		100% {
			transform: scale(1);
		}
	}

	@keyframes lds-ellipsis3 {
		0% {
			transform: scale(1);
		}

		100% {
			transform: scale(0);
		}
	}

	@keyframes lds-ellipsis2 {
		0% {
			transform: translate(0, 0);
		}

		100% {
			transform: translate(24px, 0);
		}
	}

	p{
		color: ${({ color }) => color.text};
		font-size: 1.5rem;
	}

`

const ContactContainer = styled.div`
	display:flex;
	align-items:center;
	justify-content: center;
	width: 100vw;
	padding: 7rem 8rem;
	gap: 2rem;
	color: ${({ color }) => color.text};
	font-family: 'SHPinscher';

	.sendIdea{
		width: 50%;
		display:flex; 
		flex-direction: column;
		align-self: flex-start;

		color: ${({ color }) => color.text};

			.header {
				align-self: flex-start;
				margin-bottom: 1.5rem;
				h1 {
					text-transform: lowercase;
					font-size: 4rem;
					letter-spacing: 1px;
					font-weight: 800;
				}

				h3 {
					color: ${({ color }) => color.label};
					text-transform: lowercase;
					font-size: 1.5rem;
					letter-spacing: 1.5px;
				}
			}

			.idea-form{
			display:flex; 
			flex-direction: column;
			align-items:start;
			justify-content:center;
			width: 100%;
			gap:.8rem;
			input{
				font-family: 'SHPinscher';
				background-color: ${({ color }) => color.background};
				border-radius: 3px;
				border: 2px solid ${({ color }) => color.primary};
				padding: 1rem 2rem;
				width: 70%;
				color: ${({ color }) => color.text};
				font-size: 1.2rem;
				transition: 0.1s ease-in;


				&:focus{
					outline: none;
					border-radius: 12px;
				}
			}

			textarea{
				font-family: 'SHPinscher';
				background-color: ${({ color }) => color.background};
				border-radius: 3px;
				border: 2px solid ${({ color }) => color.primary};
				padding: 1rem 2rem;
				width: 70%;
				color: ${({ color }) => color.text};
				font-size: 1.2rem;
				transition: 0.1s ease-in;
				&:focus{
					outline: none;
					border-radius: 12px;
				}
			}

			button {
						display: flex;
						align-items: center;
						justify-content: center;
						gap: 0.5rem;
						color: ${({ color }) => color.background};
						font-size: 1.75rem;
						font-family: 'SHPinscher';
						text-transform: uppercase;
						font-weight: 600;
						background-color: ${({ color }) => color.primary};
						padding: 0.5rem 1rem;
						border-radius: 3px;
						border: 2px solid ${({ color }) => color.primary};
						letter-spacing: 4.5px;
						transition: 0.1s ease-in;
						margin-top: 1.5rem;

						&:hover,
						&:active {
							border-radius: 12px;
							transform: translateY(-3px);
							box-shadow: 8px 2px 60px ${({ color }) => color.textShadow};
							background-color: ${({ color }) => color.background};
							border: 2px solid ${({ color }) => color.primary};
							color: ${({ color }) => color.text};
							cursor: pointer;
						}
					}
			
			}
	}

	.contactSocials{
		width: 50%;
		align-self: flex-start;
		display: flex;
		flex-direction: column;

		.header {
				align-self: flex-start;
				margin-bottom: 1.5rem;
				h1 {
					text-transform: lowercase;
					font-size: 4rem;
					letter-spacing: 1px;
					font-weight: 800;
				}

				h3 {
					color: ${({ color }) => color.label};
					text-transform: lowercase;
					font-size: 1.5rem;
					letter-spacing: 1.5px;
				}
			}

		.socials{
			

			.social{
				display: grid;
				grid-template-columns: 1fr 1fr;
				gap: 1rem;
				.link{
					display: flex;
					align-items:center;
					justify-content:center;
					font-size: 1.5rem;
					color: ${({ color }) => color.text};
					text-decoration: none;
					gap: 1rem;
					border-radius: 3px;
					border: 2px solid ${({ color }) => color.primary};
					width: 100%;
					padding: .5rem 1rem;
					background-color: ${({ color }) => color.background};
					transition: 0.1s ease-in;

					&:hover, &:active{
						background-color: ${({ color }) => color.text};
						/* border: 2px solid ${({ color }) => color.background}; */
						color: ${({ color }) => color.background};
						border-radius: 12px;
						transform: translateY(-3px);
						box-shadow: 8px 2px 60px ${({ color }) => color.textShadow};
					}
				}
				.last-link{
					grid-column: 1 / -1; /* Span all columns */
					
				}

				button{
					font-family: 'SHPinscher', sans-serif;
				}
			}
		}
	}

	@media (max-width: 1024px){
		flex-direction: column;
		padding: 2rem 3rem;
		margin: 3rem 0;
		.sendIdea{
			width: 100%;
			order:1;
			.header{
				align-self: center;
				text-align:center;
			}
			.idea-form{
				align-items: center;
				input{
					width: 90%;
				}

				textarea{
					width: 90%;
				}
			}
		}

		.contactSocials{
			width: 100%;
			order: 0;
		}
	}

	@media (max-width: 768px){

	}
`
